import {
    createSlice,
    PayloadAction,
    ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import { RootState } from '@config/store';
import { IClient, ISelector, PaginatedType } from '@types';
import { IClientFavoriteProduct, IClientState, ITopStats } from './types';
import { selectOptions } from '@utils/const';
import {
    getClient,
    getClients,
    getClientsFavoriteProducts,
    getTopStats,
    updateClientComment,
} from '@api/thunks/clients';

const initialState: IClientState = {
    clients: [],
    topStats: null,
    selectedClient: null,
    currentClient: null,
    favoriteProducts: [],
    selectValue: selectOptions[0],
    count: 0,
    total: 0,
    loading: 'idle',
    error: null,
};

const clientSlice = createSlice({
    name: '@@client',
    initialState,
    reducers: {
        clearClientState: () => {
            return initialState;
        },
        setSelectValue: (
            state: IClientState,
            { payload }: PayloadAction<ISelector>
        ) => {
            state.selectValue = payload;
        },
        setBloket: (
            state: IClientState,
            { payload }: PayloadAction<{ id: number; isActive: boolean }>
        ) => {
            const { id, isActive } = payload;
            const index = state.clients.findIndex(item => item.id === id);
            state.clients[index].isActive = isActive;
        },
        setRemove: (
            state: IClientState,
            { payload }: PayloadAction<number>
        ) => {
            state.clients.filter(item => item.id !== payload);
        },
        setSelectedClient: (
            state: IClientState,
            { payload }: PayloadAction<IClient>
        ) => {
            state.selectedClient = payload;
        },
    },

    extraReducers: (builder: ActionReducerMapBuilder<IClientState>) => {
        builder
            .addCase(
                getClients.fulfilled,
                (
                    state: IClientState,
                    { payload }: PayloadAction<PaginatedType<IClient>>
                ) => {
                    state.clients = payload.rows;
                    state.total = payload.count;
                }
            )
            .addCase(
                getClient.fulfilled,
                (state: IClientState, { payload }: PayloadAction<IClient>) => {
                    state.currentClient = payload;
                }
            )
            .addCase(
                getClientsFavoriteProducts.fulfilled,
                (
                    state: IClientState,
                    { payload }: PayloadAction<IClientFavoriteProduct[]>
                ) => {
                    state.favoriteProducts = payload;
                }
            )

            .addCase(
                getTopStats.fulfilled,
                (
                    state: IClientState,
                    { payload }: PayloadAction<ITopStats>
                ) => {
                    state.topStats = payload;
                }
            )
            .addCase(
                updateClientComment.fulfilled,
                (state: IClientState, { payload }: PayloadAction<IClient>) => {
                    const client = state.clients.find(
                        item => item.id === payload.id
                    );
                    if (client) {
                        client.comment = payload.comment;
                    }
                }
            )

            .addMatcher(
                action => action.type.endsWith('/pending'),
                state => {
                    state.loading = 'loading';
                    state.error = null;
                }
            )
            .addMatcher(
                action => action.type.endsWith('/rejected'),
                (state, action: PayloadAction<string>) => {
                    state.loading = 'idle';
                }
            )
            .addMatcher(
                action => action.type.endsWith('/fulfilled'),
                state => {
                    state.loading = 'idle';
                }
            );
    },
});

export const {
    clearClientState,
    setSelectValue,
    setBloket,
    setRemove,
    setSelectedClient,
} = clientSlice.actions;
export const clientReducer = clientSlice.reducer;

export const clientSelect = (state: RootState) => state.client;
